/**
 * 낭만작명 구매 내역 카드 1행에 표시할 라벨 생성 헬퍼.
 *
 * 형식: "모드명 — 식별값"
 *   - 모드명: 16개 작명 모드의 한글명 (예: 한글이름추천)
 *   - 식별값:
 *     · **추천 모드** → 카드에는 핵심 식별값만 (성씨/대상 종류 + 길이). 성별·돌림자·
 *       선호 느낌·브랜드 무드·추천 개수 같은 부가 옵션은 "추천기준보기"에 위임한다.
 *       이유: 옵션을 다 노출하면 카드 1줄이 잘림 + 추천기준보기와 중복.
 *     · **분석 모드** → 분석 대상 이름이 곧 핵심이므로 입력값을 결합해 표시.
 */

const MODE_LABEL_MAP = {
  'recommend-person-kor': '한글이름추천',
  'recommend-person-han': '한자이름추천',
  'analyze-person-kor': '한글이름분석',
  'analyze-person-han': '한자이름분석',
  'recommend-pet-animal': '반려동물이름추천',
  'recommend-pet-plant': '반려식물이름추천',
  'recommend-pet-object': '반려사물이름추천',
  'analyze-pet-animal': '반려동물이름분석',
  'analyze-pet-plant': '반려식물이름분석',
  'analyze-pet-object': '반려사물이름분석',
  'recommend-biz-biz': '상호추천',
  'analyze-biz-biz': '상호분석',
  'recommend-culture-aho': '아호추천',
  'analyze-culture-aho': '아호분석',
  'recommend-culture-nickname': '닉네임추천',
  'analyze-culture-nickname': '닉네임분석',
};

/** 옵션 key → 한글 라벨 매핑 (카드 라벨에 실제로 노출되는 그룹만 보관).
 *  성별·돌림자·선호 느낌·브랜드 무드 등 카드에 노출하지 않는 항목은 추천기준보기에서 별도 매핑 관리. */
const OPTION_LABELS = {
  animalType: { dog: '강아지', cat: '고양이', bird: '새', etc: '기타' },
  plantType: { foliage: '관엽', succulent: '다육', flower: '꽃', herb: '허브' },
  objectType: { car: '자동차', laptop: '노트북', bicycle: '자전거', furniture: '가구' },
  usage: { sns: 'SNS', game: '게임', community: '커뮤니티', general: '일반' },
};

/** 옵션 key를 한글 라벨로 변환. 매핑이 없으면 raw 값 반환 */
const labelOf = (group, key) => (key == null ? '' : (OPTION_LABELS[group]?.[key] ?? String(key)));

/** 비어있지 않은 값만 모아 join */
const join = (parts, sep = ' · ') => parts.filter((p) => p != null && p !== '').join(sep);

/** "head (sub)" 또는 "head" 형태로 합치는 헬퍼 */
const headSub = (head, sub) => join([head, sub ? `(${sub})` : ''], ' ');

const formatIdentifier = (modeKey, detailInput) => {
  if (!detailInput) return '';
  const d = detailInput;
  switch (modeKey) {
    // ── 추천 모드: 핵심 식별값만 (나머지는 추천기준보기에 위임) ──
    case 'recommend-person-kor':
      return headSub(d.surname ? `${d.surname}씨` : '', d.nameLength ? `${d.nameLength}글자` : '');
    case 'recommend-person-han':
      return d.surname ? `${d.surname}씨` : '';
    case 'recommend-pet-animal':
      return headSub(labelOf('animalType', d.animalType), d.breed);
    case 'recommend-pet-plant':
      return labelOf('plantType', d.plantType);
    case 'recommend-pet-object':
      return labelOf('objectType', d.objectType);
    case 'recommend-biz-biz':
      return headSub(d.industry || '', d.nameLength ? `${d.nameLength}글자` : '');
    case 'recommend-culture-aho':
      return headSub(d.surname ? `${d.surname}씨` : '', d.charCount ? `${d.charCount}글자` : '');
    case 'recommend-culture-nickname':
      return headSub(labelOf('usage', d.usage), d.charCount ? `${d.charCount}글자` : '');

    // ── 분석 모드: 분석 대상 이름 + 보조 정보 ──
    case 'analyze-person-kor':
    case 'analyze-person-han':
      return `${d.surname || ''}${d.firstName || ''}`;
    case 'analyze-pet-animal':
      return headSub(d.petName || '', d.animalType);
    case 'analyze-pet-plant':
      return headSub(d.plantName || '', d.plantType);
    case 'analyze-pet-object':
      return headSub(d.objectName || '', labelOf('objectType', d.objectType));
    case 'analyze-biz-biz':
      return headSub(d.bizName || '', d.industry);
    case 'analyze-culture-aho':
      return headSub(d.ahoHanja || '', d.ahoKor);
    case 'analyze-culture-nickname':
      return headSub(d.nickname || '', labelOf('usage', d.usage));
    default:
      return '';
  }
};

/**
 * @param {string} modeKey - 'recommend-person-kor' 등 type-target-detail 결합 키
 * @param {Object} detailInput - PopNamingFieldInput 결과 객체
 * @returns {string} 카드 1행 라벨. 식별값이 없으면 모드명만 반환
 */
export const formatNamingPurchaseLabel = (modeKey, detailInput) => {
  const mode = MODE_LABEL_MAP[modeKey] || '';
  const id = formatIdentifier(modeKey, detailInput);
  return id ? `${mode} — ${id}` : mode;
};

export const getNamingModeLabel = (modeKey) => MODE_LABEL_MAP[modeKey] || '';
